import type { RpcRepositoryClient } from '@ayocore/platform/server/rpc/types';
import type { SortingState } from '@tanstack/react-table';
import type { TObject } from '@sinclair/typebox';
import type { PropertiesMap } from '@ayocore/platform/common/helpers/schema-types';
import type { ColumnsMetaConfig } from '@ayocore/platform/common/helpers/column-config';
import type {
  AggregateItem,
  AggregateResult,
  GroupByConfig,
  GroupRow,
} from '@ayocore/platform/common/types/aggregate';
import { GroupsDataSource } from './GroupsDataSource';
import { TabController } from '@ayocore/platform/client/tabs/TabController';

/**
 * Простейший интерфейс стора с подпиской.
 * Используется для эксперимента отделения логики работы с репозиторием от UI.
 */
export interface SubscriptionStore<State> {
  subscribe(listener: () => void): () => void;
  getSnapshot(): State;
}

/**
 * Состояние контроллера репозитория.
 *
 * - data: текущие строки (результат загрузки)
 * - isLoading: индикатор загрузки
 * - error: ошибка последней операции (если была)
 * - meta: метаданные репозитория (схема, поля)
 * - totalCount: общее количество записей (с учётом фильтров, но без пагинации)
 */
export interface RepositoryControllerState<T> {
  data: T[];
  isLoading: boolean;
  /** Отличает успешную загрузку (включая пустую страницу) от состояния до первого запроса. */
  hasLoadedData: boolean;
  error: unknown | null;
  meta: RepositoryMeta | null;
  totalCount: number;
  /** Результат серверной агрегации (sum, avg и т.п.) */
  aggregates: AggregateResult | null;
  /** true пока агрегаты ещё загружаются (независимо от основных данных) */
  aggregatesLoading: boolean;
  /** Режим «показать только выделенные строки» — сервер фильтрует по whereIn('id', ids). */
  onlySelectedMode: boolean;
  /**
   * Выделенные бизнес-id строк (ключи rowSelection, где значение true).
   * Источник правды по выбору — контроллер: переживает рефреш через localStorage.
   */
  selectedIds: string[];
  /**
   * Номер успешной загрузки страницы: растёт на КАЖДОЙ, даже если строки
   * вернулись те же.
   *
   * Нужен тем, кто привязан к факту перезагрузки, а не к изменению данных.
   * `data` для этого не годится: селектор сравнивает её глубоко и при равных
   * строках отдаёт прежнюю ссылку — «обновил список» становится неотличимо от
   * «ничего не произошло» (так дерево `subRows` не перечитывало детей).
   */
  loadVersion: number;
}

/** Форма хранения выбора в localStorage (по одной записи на таблицу). */
interface StoredSelection {
  id: string;
  /**
   * Режим выбора. Сейчас всегда 'include' (выбрано ровно `ids`).
   * Оставлено под будущий 'exclude' (select-all: выбрано всё по фильтру, кроме `ids`).
   */
  mode: 'include' | 'exclude';
  ids: string[];
}

/**
 * Контроллер-обёртка поверх stateful RPC-репозитория.
 *
 * Идея: логика работы (фильтрация/обновление) выносится из React-компонентов в отдельный класс,
 * а UI лишь подписывается на изменения состояния и вызывает команды.
 *
 * Замечание по API репозитория:
 * - Клиентский прокси `RpcRepositoryClient<TBase>` является thenable и
 *   при ожидании (`await repo`) возвращает массив строк TBase[]. Поэтому в refresh() мы просто
 *   дожидаемся текущего состояния цепочки и сохраняем результат в `data`.
 * - Метод `filter()` принимает трансформер, который на вход получает текущий репозиторий и
 *   возвращает новый (например, с добавленными where/orderBy и т.п.). После трансформации
 *   автоматически вызывается refresh().
 */
export class RepositoryController<T extends Record<string, unknown>>
  implements SubscriptionStore<RepositoryControllerState<T>>
{
  private baseRepo: RpcRepositoryClient<T>;
  /**
   * Плоский источник для выборок ПО ВЫДЕЛЕННЫМ id (см. `selectionSource`).
   * Задаётся деревом `subRows`, когда основной репозиторий отдаёт только корни.
   */
  private flatRepo: RpcRepositoryClient<T> | null = null;
  private repoRef: RpcRepositoryClient<T>;
  private activeFilters: Record<string, (repo: RpcRepositoryClient<T>) => RpcRepositoryClient<T>> =
    {};
  private idPrefix: string = 'repository';
  private query: { page: number; pageSize: number; sorting: SortingState; search: string } = {
    page: 1,
    pageSize: 25,
    sorting: [],
    search: '',
  };

  private state: RepositoryControllerState<T> = {
    data: [],
    isLoading: false,
    hasLoadedData: false,
    error: null,
    meta: null,
    totalCount: 0,
    aggregates: null,
    aggregatesLoading: false,
    onlySelectedMode: false,
    loadVersion: 0,
    selectedIds: [],
  };

  /** Системный ключ фильтра «только выделенные». Не должен пересекаться с пользовательскими. */
  private static readonly ONLY_SELECTED_KEY = '__rt_only_selected';

  /** Ключ localStorage для персиста выбора строк (отдельно от настроек колонок). */
  private static readonly SELECTION_STORAGE_KEY = 'aya-table-selection';

  /**
   * Текущий режим выбора. 'include' — выбрано ровно `selectedIdSet`.
   * 'exclude' (select-all: выбрано всё по фильтру, кроме `selectedIdSet`) — зарезервирован
   * под будущую задачу, пока не используется.
   */
  private selectionMode: 'include' | 'exclude' = 'include';

  /** Набор выделенных бизнес-id (источник правды по выбору). */
  private selectedIdSet = new Set<string>();

  private selectionPersistScheduled = false;

  /** Конфигурация агрегатов (передаётся из RepositoryTable) */
  private aggregateConfig: AggregateItem<T>[] | null = null;

  /**
   * Данные режима группировки. Живут в отдельном сторе: у групп своя модель и
   * свой жизненный цикл, а контроллер остаётся владельцем состояния СПИСКА.
   * Инстанс создаётся сразу (пустой) — чтобы вью мог подписаться, не проверяя null.
   */
  private readonly groupsSource = new GroupsDataSource<T>();

  private listeners = new Set<() => void>();
  private emitScheduled = false;
  private filtersDirty = false;
  private lastSnapshot: RepositoryControllerState<T> | null = null;

  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Sequence-номер последнего запущенного запроса.
   * Инкрементируется в начале каждого doRefresh(). Ответы сверяются с текущим
   * значением — если номер устарел, состояние state не обновляется (защита от race
   * condition на медленной сети: более ранний запрос не должен перезаписать более поздний).
   */
  private requestSeq = 0;

  private defaultSortField: string;
  private defaultSortOrder: 'asc' | 'desc';

  /**
   * @param repo Репозиторий (stateful цепочка методов) для работы контроллера.
   * @param idPrefix Префикс для URL-параметров.
   * @param defaultPageSize Размер страницы по умолчанию.
   * @param defaultSortField Поле для сортировки по умолчанию при отсутствии сортировки (если в схеме нет created_at).
   * @param defaultSortOrder Направление сортировки по умолчанию ('asc' | 'desc').
   */
  constructor(
    repo: RpcRepositoryClient<T>,
    idPrefix?: string,
    defaultPageSize = 25,
    defaultSortField = 'created_at',
    defaultSortOrder: 'asc' | 'desc' = 'desc'
  ) {
    this.defaultSortField = defaultSortField;
    this.defaultSortOrder = defaultSortOrder;
    this.baseRepo = repo;
    this.repoRef = repo;
    if (idPrefix) this.idPrefix = idPrefix;
    this.query.pageSize = Math.max(1, defaultPageSize);
    // Инициализируем критерии из URL один раз
    this.restoreFromUrl();
    // Восстанавливаем выбор строк из localStorage (переживает рефреш)
    this.restoreSelectionFromStorage();
    // Загружаем метаданные сразу при создании контроллера
    void this.loadMeta();
  }

  /**
   * Подписка на изменения состояния. Возвращает функцию отписки.
   */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Текущий снимок состояния (immutable по контракту использования).
   * Мемоизируется для предотвращения лишних перерисовок.
   */
  getSnapshot(): RepositoryControllerState<T> {
    if (!this.lastSnapshot) {
      this.lastSnapshot = this.state;
    }
    return this.lastSnapshot;
  }

  private emit(): void {
    if (this.emitScheduled) return;
    this.emitScheduled = true;
    // Сбрасываем кэш снимка при изменении состояния
    this.lastSnapshot = null;
    queueMicrotask(() => {
      for (const l of this.listeners) l();
      this.emitScheduled = false;
    });
  }

  /**
   * Возвращает текущий репозиторий (stateful цепочка методов).
   */
  getRepository(): RpcRepositoryClient<T> {
    return this.repoRef;
  }

  /**
   * Репозиторий со всеми активными пользовательскими фильтрами, КРОМЕ указанного
   * ключа. Удобно для запросов со стороны самого фильтра — например, чтобы
   * посчитать счётчики «сколько было бы записей при каждом значении этого
   * фильтра», учитывая остальные фильтры таблицы.
   *
   * В режиме «только выделенные» пользовательские фильтры игнорируются — как и
   * в основном `rebuildRepoRef`, применяется только системный whereIn по id.
   */
  getRepositoryExcept(filterKey: string): RpcRepositoryClient<T> {
    if (this.state.onlySelectedMode) return this.composeOnlySelectedRepo();
    return Object.entries(this.activeFilters)
      .filter(([key]) => key !== filterKey)
      .reduce((repo, [, filterFn]) => filterFn(repo), this.baseRepo);
  }

  /**
   * Репозиторий в том виде, в каком таблицу видит пользователь: фильтры, поиск
   * и выбранная им сортировка — без пагинации. Для терминальных методов, которые
   * должны работать «по тому, что на экране»: выгрузки, отчёты, массовые операции.
   *
   * `getRepository()` для этого не подходит: в нём только фильтры (поиск и
   * сортировку контроллер накладывает отдельно, при выборке данных), и он
   * пересобирается лишь на refresh. Здесь цепочка собирается из актуального
   * состояния.
   *
   * Сортировка по умолчанию (`defaultSortField`) сюда НЕ входит — это настройка
   * отображения таблицы, а не выбор пользователя. Если пользователь ничего не
   * выбрал, порядок решает серверный метод: например, выгрузка сортирует по
   * своему полю. Поэтому серверному методу не нужно сбрасывать сортировку
   * (`clearOrder`) — достаточно дописать свои ключи после пришедших.
   *
   * @param options.selectedOnly Только выделенные строки (по всем страницам) и
   *   сортировка пользователя. Фильтры и поиск не применяются: выделение
   *   переживает смену фильтра, и иначе часть выделенных строк молча выпала бы.
   */
  getViewRepository(options: { selectedOnly?: boolean } = {}): RpcRepositoryClient<T> {
    const { search, sorting } = this.query;
    if (options.selectedOnly) {
      const ids = this.coerceIdsBySchema(this.getSelectedIds());
      const source = this.selectionSource();
      const selected = source.whereIn?.('id', ids) ?? source;
      return this.applySearchAndSorting(selected, '', sorting);
    }
    return this.applySearchAndSorting(this.composeFilteredRepo(), search, sorting);
  }

  /** Применяет собранные параметры к URL и синхронизирует активную вкладку. */
  private applyUrl(sp: URLSearchParams): void {
    const newUrl = `${window.location.pathname}?${sp.toString()}`;
    window.history.replaceState(null, '', newUrl);

    // Синхронизируем URL активной вкладки в микро-задаче
    queueMicrotask(() => {
      try {
        const key = window.location.pathname;
        const ctrl = TabController.getInstance();
        ctrl.setUrlAndActivate(key, newUrl);
      } catch {
        /* noop */
      }
    });
  }

  /** Унифицированное обновление строки запроса URL. */
  private updateSearchParams(patch: (sp: URLSearchParams) => void): void {
    try {
      const sp = new URLSearchParams(window.location.search);
      patch(sp);
      this.applyUrl(sp);
    } catch {
      /* noop */
    }
  }

  /**
   * Возвращает строковое значение параметра фильтра из URL для текущей таблицы.
   * Ключ формируется как `${idPrefix}_f_${paramKey}`.
   */
  getUrlParam(paramKey: string): string | null {
    try {
      const sp = new URLSearchParams(window.location.search);
      return sp.get(`${this.idPrefix}_f_${paramKey}`);
    } catch {
      return null;
    }
  }

  /**
   * Применяет патч параметров фильтров в URL без пробуждения Router и синхронизирует вкладку.
   * Для каждого ключа `k` из `params` используется namespaced имя `${idPrefix}_f_${k}`.
   * Значение null или пустая строка приводит к удалению параметра.
   */
  setUrlParams(params: Record<string, string | null>): void {
    this.updateSearchParams(sp => {
      Object.keys(params).forEach(k => {
        const namespaced = `${this.idPrefix}_f_${k}`;
        const v = params[k];
        if (v === null || v === '') sp.delete(namespaced);
        else sp.set(namespaced, v);
      });
    });
  }

  /**
   * Загружает метаинформацию о репозитории и кэширует её до смены репозитория.
   */
  private async loadMeta(): Promise<void> {
    try {
      const meta = await this.repoRef.meta?.();
      if (meta) {
        this.state = { ...this.state, meta: meta as RepositoryMeta };
        this.emit();
      }
    } catch {
      // meta опциональна; проглатываем ошибку, не ломая UI
    }
  }

  /** Читает критерии из URL (только при создании контроллера). */
  private restoreFromUrl(): void {
    try {
      const sp = new URLSearchParams(window.location.search);
      const p = Number(sp.get(`${this.idPrefix}_page`) || '1');
      const ps = Number(sp.get(`${this.idPrefix}_ps`) || String(this.query.pageSize));
      const s = sp.get(`${this.idPrefix}_sort`);
      const searchTerm = sp.get(`${this.idPrefix}_search`) || '';
      if (!Number.isNaN(p)) this.query.page = Math.max(1, p);
      if (!Number.isNaN(ps)) this.query.pageSize = Math.max(1, ps);
      if (s) {
        try {
          const parsed = JSON.parse(s);
          if (Array.isArray(parsed)) this.query.sorting = parsed as SortingState;
        } catch {
          /* noop */
        }
      }
      // Восстанавливаем поисковый запрос из URL
      if (searchTerm) {
        this.query.search = searchTerm;
      }
    } catch {
      /* noop */
    }
  }

  /**
   * Пишет критерии таблицы (страница, размер, сортировка, поиск) в URL без пробуждения Router.
   *
   * Почему не используем router.navigate:
   * - нам важно избежать лишних перерисовок приложения при изменении сортировки/пагинации,
   *   поэтому используем window.history.replaceState.
   *
   * Синхронизация с вкладками:
   * - так как replaceState не меняет location в Router, хук useTabSync не узнаёт об изменении search.
   * - чтобы вкладка сохраняла актуальный URL (включая сортировку/пагинацию/поиск) и оставалась активной,
   *   дополнительно обновляем URL вкладки через TabController.setUrl(...) и фиксируем активность через
   *   TabController.activate(...).
   */
  private syncUrl(): void {
    this.updateSearchParams(sp => {
      sp.set(`${this.idPrefix}_page`, String(this.query.page));
      sp.set(`${this.idPrefix}_ps`, String(this.query.pageSize));
      if (this.query.sorting.length > 0) {
        sp.set(`${this.idPrefix}_sort`, JSON.stringify(this.query.sorting));
      } else {
        sp.delete(`${this.idPrefix}_sort`);
      }
      // Сохраняем поисковый запрос в URL
      if (this.query.search) {
        sp.set(`${this.idPrefix}_search`, this.query.search);
      } else {
        sp.delete(`${this.idPrefix}_search`);
      }
    });
  }

  /**
   * Принудительное обновление данных из текущей цепочки репозитория.
   * Загружает данные и общее количество записей параллельно.
   */
  refresh(): void {
    // Если таймер уже есть — сбросить
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    // Установить новый таймер
    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null;
      void this.doRefresh(); // Просто void, без Promise wrapper
    }, 150); // Уменьшили до 50ms
  }

  /**
   * Цепочка с активными фильтрами, поиском и сортировкой — без пагинации.
   * Один источник для всех запросов страницы (строки, count, агрегаты, группы и
   * дети групп): иначе срез «по фильтру» разъехался бы между ними.
   *
   * @param search Поисковая строка (в режиме «только выделенные» не применяется)
   * @param sorting Состояние сортировки таблицы
   */
  private buildBaseQuery(search: string, sorting: SortingState): RpcRepositoryClient<T> {
    const baseQuery = this.applySearchAndSorting(this.repoRef, search, sorting);
    if (sorting.length > 0) return baseQuery;
    return baseQuery.orderBy?.(this.defaultSortField, this.defaultSortOrder) ?? baseQuery;
  }

  /**
   * Накладывает на цепочку поиск и сортировку, выбранную пользователем.
   * Поиск в режиме «только выделенные» не применяется — как и на экране.
   */
  private applySearchAndSorting(
    repo: RpcRepositoryClient<T>,
    search: string,
    sorting: SortingState
  ): RpcRepositoryClient<T> {
    let query = repo;
    if (search && !this.state.onlySelectedMode) {
      query = query.search?.(search) ?? query;
    }
    if (sorting.length > 0) {
      const { id, desc } = sorting[0];
      query = query.orderBy?.(id, desc ? 'desc' : 'asc') ?? query;
    }
    return query;
  }

  async doRefresh() {
    // Пересобираем фильтры только если они изменились
    if (this.filtersDirty) {
      this.rebuildRepoRef();
      this.filtersDirty = false;
    }

    // Маркируем запрос: любой более поздний refresh инкрементирует seq и «стирает» этот.
    const reqId = ++this.requestSeq;

    // UI обновление (синхронно)
    this.state = { ...this.state, isLoading: true, error: null };
    this.emit();

    try {
      // Подготовка запроса (синхронно)
      const { page, pageSize, sorting, search } = this.query;

      const baseQuery = this.buildBaseQuery(search, sorting);

      // Параллельные запросы: count (без пагинации) и data (с пагинацией)
      const offset = (page - 1) * pageSize;
      const dataQuery = baseQuery.offset?.(offset) ?? baseQuery;
      const paginatedQuery = dataQuery.limit?.(pageSize) ?? dataQuery;

      const hasAggregates = !!this.aggregateConfig?.length;

      // Запускаем агрегаты независимо — не блокируем отображение таблицы
      if (hasAggregates) {
        this.state = { ...this.state, aggregatesLoading: true, aggregates: null };
        const aggregateQuery = baseQuery.aggregate?.(this.aggregateConfig!);
        if (aggregateQuery) {
          aggregateQuery
            .then(aggregates => {
              if (reqId !== this.requestSeq) return;
              this.state = { ...this.state, aggregates, aggregatesLoading: false };
              this.emit();
            })
            .catch(() => {
              if (reqId !== this.requestSeq) return;
              this.state = { ...this.state, aggregatesLoading: false };
              this.emit();
            });
        }
      }

      // Режим группировки: строки таблицы — это группы, и считает их БД.
      // Страница и total тоже по группам, поэтому обычная выборка строк здесь
      // не нужна: она вернула бы страницу сущностей, которую никто не покажет.
      if (this.groupsSource.isEnabled()) {
        const groupsTotal = await this.groupsSource.loadPage(baseQuery, {
          limit: pageSize,
          offset,
          isStale: () => reqId !== this.requestSeq,
        });
        if (groupsTotal === null) return;

        this.state = {
          ...this.state,
          data: [],
          totalCount: groupsTotal,
          isLoading: false,
          hasLoadedData: true,
          error: null,
          loadVersion: this.state.loadVersion + 1,
        };
        this.emit();
        return;
      }

      const [rows, total] = await Promise.all([
        paginatedQuery,
        baseQuery.count?.() ?? Promise.resolve(0),
      ]);

      // Если пока мы ждали ответ, успел стартовать более свежий запрос — игнорируем.
      if (reqId !== this.requestSeq) return;

      // Обновление результата (синхронно)
      this.state = {
        ...this.state,
        data: rows,
        totalCount: total,
        isLoading: false,
        hasLoadedData: true,
        error: null,
        loadVersion: this.state.loadVersion + 1,
      };
      this.emit();
    } catch (err) {
      if (reqId !== this.requestSeq) return;
      // Ответы уже стартовавших догрузок отброшены как устаревшие (seq вырос),
      // поэтому их плейсхолдеры надо снять — иначе группа навсегда «грузится».
      if (this.groupsSource.isEnabled()) {
        this.groupsSource.resetChildren();
      }
      // Обновление ошибки (синхронно)
      this.state = { ...this.state, isLoading: false, error: err };
      this.emit();
    }
  }

  /**
   * Применяет фильтр к репозиторию. Если transform === null, фильтр удаляется.
   *
   * По умолчанию любое изменение фильтра возвращает таблицу на 1-ю страницу
   * (симметрично с setSorting/setSearch/setPageSize) — иначе пользователь
   * на странице 5 после смены фильтра видит «пусто», хотя данные есть на 1-й.
   * Передайте `{ resetPage: false }` для первичной установки на mount, когда
   * фильтр восстанавливается из URL и сохранённую страницу терять нельзя
   * (этим управляет обёртка useRepositoryFilter).
   *
   * @param key Ключ фильтра (например, 'manager', 'status', 'partner')
   * @param transform Функция трансформации репозитория или null для удаления фильтра
   * @param options.resetPage Сбросить ли пагинацию на 1 (по умолчанию true)
   */
  filter(
    key: string,
    transform: ((repo: RpcRepositoryClient<T>) => RpcRepositoryClient<T>) | null,
    options?: { resetPage?: boolean }
  ): void {
    // Пользовательский фильтр сбрасывает режим «только выделенные» (UX-контракт).
    // Системный ключ — это сам механизм включения режима, его не трогаем.
    if (key !== RepositoryController.ONLY_SELECTED_KEY) {
      this.clearOnlySelectedInternal();
    }

    if (transform === null) {
      delete this.activeFilters[key];
    } else {
      this.activeFilters[key] = transform;
    }

    // По умолчанию любое изменение фильтра возвращает на 1-ю страницу
    // (симметрично setSorting/setSearch/setPageSize). UI-обёртка
    // useRepositoryFilter передаёт `resetPage: false` для первичной установки
    // на mount (когда фильтр восстанавливается из URL), чтобы не потерять
    // сохранённую страницу.
    if (options?.resetPage !== false) {
      this.query.page = 1;
      this.syncUrl();
    }

    // Отмечаем что фильтры изменились, пересоберем в doRefresh()
    this.filtersDirty = true;
    void this.refresh();
  }

  /**
   * Включает/обновляет/выключает режим «только выделенные строки».
   * - Если передан непустой массив ID — ставит фильтр whereIn('id', ids) и включает режим.
   * - Если передан пустой массив или null — выключает режим и убирает фильтр.
   * Сортировка/пагинация продолжают работать поверх, пользовательские фильтры — сбрасывают режим.
   */
  setOnlySelected(ids: unknown[] | null): void {
    const key = RepositoryController.ONLY_SELECTED_KEY;

    if (!ids || ids.length === 0) {
      if (!this.state.onlySelectedMode && !(key in this.activeFilters)) return;
      delete this.activeFilters[key];
      this.state = { ...this.state, onlySelectedMode: false };
      this.filtersDirty = true;
      this.query.page = 1;
      this.emit();
      void this.refresh();
      return;
    }

    // Ключи rowSelection всегда строки (требование tanstack-table.getRowId),
    // но поле id в схеме может быть integer — приводим по meta-схеме.
    const idsSnapshot = this.coerceIdsBySchema(ids);
    this.activeFilters[key] = repo => repo.whereIn?.('id', idsSnapshot) ?? repo;
    this.state = { ...this.state, onlySelectedMode: true };
    this.filtersDirty = true;
    this.query.page = 1;
    this.emit();
    void this.refresh();
  }

  /**
   * Приводит массив ID-строк к типу поля id согласно мета-схеме репозитория.
   * Если id в схеме — integer, строки конвертятся в числа (NaN отсеиваются).
   * Иначе (string/uuid) возвращаются как есть.
   */
  private coerceIdsBySchema(ids: unknown[]): unknown[] {
    const props = this.state.meta?.schema?.properties as Record<string, unknown> | undefined;
    const idSchema = props?.id as Record<string, unknown> | undefined;
    if (!idSchema) return [...ids];

    const isInteger =
      idSchema.type === 'integer' ||
      (Array.isArray(idSchema.anyOf) &&
        idSchema.anyOf.some(t => (t as Record<string, unknown>).type === 'integer'));

    if (!isInteger) return [...ids];

    const coerced: number[] = [];
    for (const v of ids) {
      const n = typeof v === 'number' ? v : Number(v);
      if (Number.isFinite(n)) coerced.push(n);
    }
    return coerced;
  }

  /** Внутренний сброс режима без эмита refresh — вызывается из других сеттеров. */
  private clearOnlySelectedInternal(): void {
    const key = RepositoryController.ONLY_SELECTED_KEY;
    if (!(key in this.activeFilters) && !this.state.onlySelectedMode) return;
    delete this.activeFilters[key];
    this.state = { ...this.state, onlySelectedMode: false };
    this.filtersDirty = true;
    // emit и refresh произойдут в вызывающем сеттере
  }

  /**
   * Пересобирает цепочку фильтров из исходного репозитория.
   * В режиме «только выделенные» пропускает пользовательские фильтры — применяется
   * только системный whereIn('id', ids), чтобы юзер видел ровно выделенные записи.
   * Вызывается только когда фильтры действительно изменились.
   */
  private rebuildRepoRef(): void {
    this.repoRef = this.composeFilteredRepo();
  }

  /**
   * Собирает цепочку активных фильтров заново, ничего не кэшируя.
   * В режиме «только выделенные» — только системный whereIn('id', ids).
   */
  private composeFilteredRepo(): RpcRepositoryClient<T> {
    if (this.state.onlySelectedMode) return this.composeOnlySelectedRepo();
    return Object.values(this.activeFilters).reduce(
      (repo, filterFn) => filterFn(repo),
      this.baseRepo
    );
  }

  // Управление критериями (единые точки входа)
  setSorting(sorting: SortingState): void {
    this.query.sorting = sorting;
    this.query.page = 1;
    this.syncUrl();
    void this.refresh();
  }

  setPage(page: number): void {
    this.query.page = Math.max(1, page);
    this.syncUrl();
    void this.refresh();
  }

  setPageSize(pageSize: number): void {
    this.query.pageSize = Math.max(1, pageSize);
    this.query.page = 1;
    this.syncUrl();
    void this.refresh();
  }

  setSearch(search: string): void {
    // Поиск сбрасывает режим «только выделенные» — UX-контракт.
    this.clearOnlySelectedInternal();
    this.query.search = search;
    this.query.page = 1;
    this.syncUrl();
    void this.refresh();
  }

  /**
   * Устанавливает конфигурацию агрегатов. При следующем refresh() результат будет загружен.
   */
  setAggregateConfig(config: AggregateItem<T>[] | null): void {
    this.aggregateConfig = config;
  }

  /**
   * Включает режим группировки (null — выключает). При следующем refresh()
   * таблица загрузит группы вместо строк.
   *
   * Пагинация в этом режиме считается по ГРУППАМ: `limit/offset` уходят в
   * aggregateBy, а `totalCount` берётся из countGroups.
   */
  setGroupConfig(config: GroupByConfig<T> | null): void {
    this.groupsSource.setConfig(config);
  }

  /** Включён ли режим группировки (для UI — что скрыть/отключить). */
  isGrouped(): boolean {
    return this.groupsSource.isEnabled();
  }

  /** Стор данных группировки — на него подписывается групповое представление. */
  getGroupsSource(): GroupsDataSource<T> {
    return this.groupsSource;
  }

  /**
   * Догружает строки одной группы. Цепочку с фильтрами и поиском собирает
   * контроллер (он ими владеет), загрузку и кеш ведёт источник групп.
   *
   * @param value Значение ключа группы (null — группа «пусто»)
   * @param limit Максимум строк за раз
   */
  async loadGroupChildren(value: GroupRow['value'], limit?: number): Promise<void> {
    if (!this.groupsSource.isEnabled()) return;

    if (this.filtersDirty) {
      this.rebuildRepoRef();
      this.filtersDirty = false;
    }

    // Метка выборки, для которой грузим детей. Любой refresh (фильтр, поиск,
    // страница) инкрементирует requestSeq и очищает карту детей — поздний ответ
    // с этой меткой уже неактуален и писаться не должен, иначе под группой
    // оказались бы строки от прошлого фильтра.
    const reqId = this.requestSeq;
    const { search, sorting } = this.query;

    await this.groupsSource.loadChildren(this.buildBaseQuery(search, sorting), value, {
      limit,
      isStale: () => reqId !== this.requestSeq,
    });
  }

  /**
   * Выбранные строки среди подгруженных детей групп — для bulkActions в режиме
   * группировки. В обычном режиме строки достаёт RepositoryTableWrapper из
   * текущей страницы; здесь страница состоит из групп, а сущности лежат в
   * раскрытых группах, поэтому источник другой, а контракт обработчика тот же.
   */
  getSelectedGroupChildRows(getRowKey: (row: T) => string): T[] {
    return this.groupsSource.getSelectedRows(this.state.selectedIds, getRowKey);
  }

  getQuery(): { page: number; pageSize: number; sorting: SortingState; search: string } {
    return {
      page: this.query.page,
      pageSize: this.query.pageSize,
      sorting: [...this.query.sorting],
      search: this.query.search,
    };
  }

  // === Выбор строк (источник правды + персист в localStorage) ===

  /**
   * Принимает rowSelection-запись от таблицы (TanStack), сохраняет выбранные
   * бизнес-id и персистит их. Ключи со значением false игнорируются.
   */
  setRowSelection(rowSelection: Record<string, boolean>): void {
    const next = new Set<string>();
    for (const key of Object.keys(rowSelection)) {
      if (rowSelection[key]) next.add(key);
    }
    // Не эмитим/не персистим если набор не изменился (избегаем лишних циклов).
    if (this.isSameSelection(next)) return;
    this.selectedIdSet = next;
    this.state = { ...this.state, selectedIds: [...next] };
    this.emit();
    this.scheduleSelectionPersist();
  }

  /** Список выделенных бизнес-id. */
  getSelectedIds(): string[] {
    return [...this.selectedIdSet];
  }

  /** Выбор в формате rowSelection для сидинга таблицы (Record<id, true>). */
  getRowSelection(): Record<string, boolean> {
    const record: Record<string, boolean> = {};
    for (const id of this.selectedIdSet) record[id] = true;
    return record;
  }

  /**
   * Совпадает ли внешний rowSelection-набор (напр. галочки TanStack) с текущим
   * выбором контроллера по составу; ключи со значением false игнорируются.
   * Используется обратной синхронизацией «контроллер → галочки таблицы»
   * (RepositoryTable): выбор меняют и кастомные вьюхи напрямую в контроллере,
   * guard отсекает холостые обновления стора галочек.
   */
  isSelectionEqual(rowSelection: Record<string, boolean>): boolean {
    const next = new Set<string>();
    for (const key of Object.keys(rowSelection)) {
      if (rowSelection[key]) next.add(key);
    }
    return this.isSameSelection(next);
  }

  /** Сравнивает переданный набор с текущим выбором (по составу). */
  private isSameSelection(next: Set<string>): boolean {
    if (next.size !== this.selectedIdSet.size) return false;
    for (const id of next) {
      if (!this.selectedIdSet.has(id)) return false;
    }
    return true;
  }

  private restoreSelectionFromStorage(): void {
    try {
      const stored = this.loadAllSelections().find(s => s.id === this.idPrefix);
      if (!stored || !Array.isArray(stored.ids)) return;
      this.selectedIdSet = new Set(stored.ids.map(String));
      this.state = { ...this.state, selectedIds: [...this.selectedIdSet] };
    } catch {
      /* noop */
    }
  }

  private scheduleSelectionPersist(): void {
    if (this.selectionPersistScheduled) return;
    this.selectionPersistScheduled = true;
    queueMicrotask(() => {
      this.persistSelection();
      this.selectionPersistScheduled = false;
    });
  }

  private persistSelection(): void {
    try {
      const all = this.loadAllSelections().filter(s => s.id !== this.idPrefix);
      // Пустой выбор не храним — удаляем запись, чтобы storage не разрастался.
      if (this.selectedIdSet.size > 0) {
        all.push({ id: this.idPrefix, mode: this.selectionMode, ids: [...this.selectedIdSet] });
      }
      localStorage.setItem(RepositoryController.SELECTION_STORAGE_KEY, JSON.stringify(all));
    } catch {
      /* noop */
    }
  }

  private loadAllSelections(): StoredSelection[] {
    try {
      const raw = localStorage.getItem(RepositoryController.SELECTION_STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /**
   * Заменить исходный репозиторий. Обновляет baseRepo, отмечает фильтры
   * dirty (чтобы активные пользовательские фильтры переложились на новую базу),
   * триггерит refresh().
   *
   * Важно: meta не обнуляем — подразумевается смена цепочки фильтров на том же
   * типе репозитория, схема не меняется. Обнуление meta вызывало ремаунт
   * дочерних компонентов (колонки/фильтры зависят от неё).
   *
   * Пагинация СОХРАНЯЕТСЯ.
   * Раньше тут стоял `this.query.page = 1` — эвристика «новый baseRepo = новый
   * контекст = с 1-й страницы», ориентированная на picker'ы с категорийным
   * деревом. Но в RepositoryTable.useEffect setRepository дёргается на КАЖДОЙ
   * смене ref у `repository` prop'а — а каллеры часто строят цепочку прямо в
   * рендере (`useXxxRepository().withA().withB()`), без useMemo. Любой
   * ре-рендер парента → новая ссылка → setRepository → page=1 → юзера
   * выбрасывает на 1-ю страницу при закрытии любого drawer/модалки.
   *
   * Кому реально нужен сброс при смене контекста (picker меняет категорию) —
   * пусть зовёт `controller.setPage(1)` явно после `setRepository`.
   */
  setRepository(repo: RpcRepositoryClient<T>): void {
    this.baseRepo = repo;
    this.filtersDirty = true;
    void this.refresh();
  }

  /**
   * Плоский репозиторий дерева (`RepositoryTableSubRows.flatRepository`): все
   * строки — и корни, и дети. Нужен, когда основной репозиторий отдаёт только
   * корни (дети показываются раскрытием): выборки по выделенным id иначе
   * потеряли бы выделенных детей. null — источник выборок основной репозиторий.
   * Перезагружает данные, только если сейчас включён режим «только выделенные».
   */
  setFlatRepository(repo: RpcRepositoryClient<T> | null): void {
    this.flatRepo = repo;
    if (!this.state.onlySelectedMode) return;
    this.filtersDirty = true;
    void this.refresh();
  }

  /**
   * Источник для выборок ПО ВЫДЕЛЕННЫМ id — режим «только выделенные» и
   * `getViewRepository({ selectedOnly })`: плоский репозиторий дерева, если он
   * задан, иначе основной. Фильтры пользователя здесь не применяются: выделение
   * переживает смену фильтра.
   */
  private selectionSource(): RpcRepositoryClient<T> {
    return this.flatRepo ?? this.baseRepo;
  }

  /** Режим «только выделенные»: системный whereIn по id поверх источника выборок. */
  private composeOnlySelectedRepo(): RpcRepositoryClient<T> {
    const source = this.selectionSource();
    const onlySelected = this.activeFilters[RepositoryController.ONLY_SELECTED_KEY];
    return onlySelected ? onlySelected(source) : source;
  }
}

/**
 * Метаданные репозитория, используемые для генерации колонок и подсказок в UI.
 */
export interface RepositoryMeta {
  schema: TObject<PropertiesMap> | { properties: Record<string, unknown> };
  tableName: string;
  dataFields: string[];
  /** Конфигурация колонок таблицы (порядок, видимость) */
  columnsConfig: ColumnsMetaConfig;
}
